import { APP_CONFIG } from '@/constants/app';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';

export interface EditionOption {
  value: string;
  name: string;
}

export interface EditionsOptionsResponse {
  data: {
    options: EditionOption[];
    folderUrl: string;
    message: string;
  };
  meta: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
}

export interface EditionVisualizeRequest {
  editionValue: string;
  startMonth: number;
  startYear: number;
  endMonth: number;
  endYear: number;
}

export interface EditionVisualizeBatchRequest {
  editionValues: string[];
  startMonth: number;
  startYear: number;
  endMonth: number;
  endYear: number;
  saveToGed?: boolean;
  sendByEmail?: boolean;
  emailRecipients?: string[];
}
export interface MonthlyData {
  month: number;
  year: number;
  data: { [key: string]: string }[];
  totalRows: number;
}

export interface EditionVisualizeResponse {
  data: {
    editionType: string;
    editionName: string;
    startPeriod: string;
    endPeriod: string;
    monthlyData: MonthlyData[];
    totalMonths: number;
    synthesis?: {
      startPeriod: string;
      endPeriod: string;
      data: { [key: string]: string }[];
      totalRows: number;
    };
    message: string;
  };
  meta: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
}

export interface BatchEditionResult {
  editionValue: string;
  success: boolean;
  data?: EditionVisualizeResponse['data'];
  error?: string;
  savedToGed: boolean;
  gedDocumentUuid?: string;
}

export interface EditionVisualizeBatchResponse {
  data: {
    results: BatchEditionResult[];
    totalEditions: number;
    successCount: number;
    errorCount: number;
    message: string;
  };
  meta: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
}

export interface BulkGenerateRequest {
  folderUuids: string[];
  editionValues: string[];
  startMonth: number;
  startYear: number;
  endMonth: number;
  endYear: number;
}

export interface BulkGenerateResponse {
  data: {
    batchId: string;
    totalFolders: number;
    totalEditions: number;
    message: string;
  };
  meta: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
}

export interface BulkGenerateFolderResult {
  folderUuid: string;
  folderName: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error?: string;
  successCount?: number;
  errorCount?: number;
}

export interface BulkGenerateProgressResponse {
  data: {
    batchId: string;
    total: number;
    completed: number;
    failed: number;
    status: 'processing' | 'completed';
    results: BulkGenerateFolderResult[];
  };
  meta: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
}

// Create axios instance for editions service
const editionsApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl + '/editions',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup automatic token refresh interceptors
tokenManager.setupInterceptors(editionsApi);

export const editionsService = {
  getFolderOptions: async (
    folderUuid: string
  ): Promise<EditionsOptionsResponse> => {
    const response = await editionsApi.get<EditionsOptionsResponse>(
      `/folders/${folderUuid}/options`
    );
    return response.data;
  },

  visualizeEdition: async (
    folderUuid: string,
    request: EditionVisualizeRequest
  ): Promise<EditionVisualizeResponse> => {
    const response = await editionsApi.post<EditionVisualizeResponse>(
      `/folders/${folderUuid}/visualize`,
      request
    );
    return response.data;
  },

  visualizeBatch: async (
    folderUuid: string,
    request: EditionVisualizeBatchRequest
  ): Promise<EditionVisualizeBatchResponse> => {
    const response = await editionsApi.post<EditionVisualizeBatchResponse>(
      `/folders/${folderUuid}/visualize-batch`,
      request
    );
    return response.data;
  },

  bulkGenerate: async (
    request: BulkGenerateRequest
  ): Promise<BulkGenerateResponse> => {
    const response = await editionsApi.post<BulkGenerateResponse>(
      '/bulk-generate',
      request
    );
    return response.data;
  },

  getBulkProgress: async (
    batchId: string
  ): Promise<BulkGenerateProgressResponse> => {
    const response = await editionsApi.get<BulkGenerateProgressResponse>(
      `/bulk-generate/${batchId}`
    );
    return response.data;
  },
};
